import { Star } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage, Button, EmptyState, Skeleton } from '@teach/ui'
import { formatDateOrDash } from '@/lib/formatDate'
import { useLanguage } from '@/hooks/useLanguage'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { useGetCourseReviewsInfiniteQuery } from '../analyticsApi'

const DATE_FORMAT = {
  ru: new Intl.DateTimeFormat('ru', { day: 'numeric', month: 'long', year: 'numeric' }),
  en: new Intl.DateTimeFormat('en', { day: 'numeric', month: 'long', year: 'numeric' }),
}

const Stars = ({ value }: { value: number }) => {
  const m = useMessages(messages)
  return (
    <span className="flex items-center gap-0.5 text-warning" aria-label={m.rating(value)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`size-4 ${n <= value ? 'fill-current' : 'opacity-30'}`} />
      ))}
    </span>
  )
}

/** Отзывы учеников о курсе: список постранично, «Показать ещё» подгружает следующие. */
export const ReviewsBlock = ({ courseId }: { courseId: number }) => {
  const { language } = useLanguage()
  const m = useMessages(messages)
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } =
    useGetCourseReviewsInfiniteQuery(courseId)
  const reviews = data?.pages.flatMap((p) => p.items) ?? []

  if (isLoading) return <Skeleton className="h-40 w-full rounded-3xl" />
  if (isError)
    return (
      <div className="flex items-center gap-4 rounded-3xl bg-foreground/5 p-5 text-sm">
        {m.loadFailed}
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          {m.retry}
        </Button>
      </div>
    )
  if (reviews.length === 0)
    return (
      // Маскот по центру свободной области вкладки, крупно: под вкладками остаётся весь экран
      <div className="flex min-h-[calc(100dvh-22rem)] items-center justify-center">
        <EmptyState size="lg" title={m.empty} />
      </div>
    )

  return (
    <div className="space-y-3">
      {reviews.map((r) => {
        const name = r.user.username || r.user.telegramUsername || m.learner
        return (
          <article key={r.id} className="rounded-3xl bg-foreground/5 p-5">
            <div className="flex items-center gap-3">
              <Avatar className="size-10">
                {r.user.avatar ? <AvatarImage src={r.user.avatar} alt="" /> : null}
                <AvatarFallback>{name.slice(0, 1).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatDateOrDash(r.createdAt, DATE_FORMAT[language])}
                </div>
              </div>
              <Stars value={r.rating} />
            </div>
            {r.text ? <p className="mt-3 whitespace-pre-wrap text-[15px]">{r.text}</p> : null}
          </article>
        )
      })}
      {hasNextPage ? (
        <Button
          variant="outline"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage ? m.loading : m.showMore}
        </Button>
      ) : null}
    </div>
  )
}
